import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Building2, ScrollText, Loader2 } from "lucide-react";
import { getMeeting } from "@/lib/api";
import { toast } from "sonner";

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMeeting(id)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Meeting not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-5xl mx-auto p-10 text-zinc-500"><Loader2 size={14} className="inline animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="meeting-detail-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="label-eyebrow mb-2">Meeting · {data.meeting_type.toUpperCase()}</div>
      <h1 className="font-heading text-4xl font-light tracking-tight mb-2">
        {data.meeting_type === "agm" ? "Annual General Meeting" :
         data.meeting_type === "egm" ? "Extra-Ordinary General Meeting" :
         "Board Meeting"}
      </h1>
      <p className="text-sm text-zinc-500 mb-8">
        <Building2 size={12} className="inline mr-1" /> {data.company_name} ·{" "}
        <Calendar size={12} className="inline mx-1" /> {data.meeting_date}
        {data.meeting_time ? ` at ${data.meeting_time}` : ""}
      </p>

      <div className="border border-zinc-200 bg-white">
        <div className="p-5 border-b border-zinc-200 flex items-center gap-2">
          <ScrollText size={16} className="text-[#002FA7]" />
          <h3 className="font-heading text-lg font-medium tracking-tight">
            Agenda · {data.resolutions.length} Resolution{data.resolutions.length > 1 ? "s" : ""}
          </h3>
        </div>
        <ul className="divide-y divide-zinc-100" data-testid="meeting-resolutions">
          {data.resolutions.map((r, i) => (
            <li key={r.id}>
              <Link
                to={`/resolutions/${r.id}`}
                className="block p-5 hover:bg-zinc-50 transition-colors"
                data-testid={`meeting-resolution-${i}`}
              >
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <span className="font-mono-plex text-xs text-[#002FA7]">{r.resolution_number}</span>
                  {r.is_special_resolution && (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[#002FA7] text-white">Special</span>
                  )}
                </div>
                <div className="font-medium text-sm">{r.resolution_title}</div>
                <div className="text-xs text-zinc-500 mt-1 line-clamp-2 leading-relaxed">
                  {(r.resolution_body || "").slice(0, 200)}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
